import {
  API_MS_CREATE_LEAVE_REQUEST,
  API_MS_GET_ALL_LEAVE_REQUESTS,
  API_MS_LEAVE_BALANCE_STATS,
  API_MS_LEAVE_POLICY,
  API_MS_LEAVE_REQUEST_SUMMARY,
  API_MS_LEAVE_REQUEST_TYPES,
} from "@/constants/apiEndpoints";
import type {
  ICancelMSLeaveRequestParams,
  TCancelMSLeaveRequestResponse,
  TGetMSAllLeavesRequest,
  TGetMSAllLeavesResponse,
  TGetMSLeaveRequestByIdResponse,
  TMSCreateLeaveRequestData,
  TMSCreateLeaveRequestResponse,
  TMsGetLeavePolicyResponse,
  TUpdateMSLeaveRequestParams,
  TUpdateMSLeaveRequestResponse,
  TMSRequestAmendmentParams,
  TMSRequestAmendmentResponse,
  TMSCancelAmendmentParams,
  TMSCancelAmendmentResponse,
  ILeaveType,
  ILeaveSummary,
  MSLeaveBalanceStatsData,
} from "@/types/(my-space)/leaves.interface";
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";

export const leaveRequestApi = createApi({
  reducerPath: "leaveRequestApi",
  refetchOnFocus: false,
  baseQuery: dynamicBaseQuery,
  tagTypes: ["msGetLeaveBalanceStats", "msGetAllLeaveRequests", "msGetLeaveRequestById", "updateMSLeaveRequest"],
  endpoints: (builder) => ({
    msGetLeaveBalanceStats: builder.query<IApiResponse<MSLeaveBalanceStatsData[]>, void>({
      query: () => ({
        url: API_MS_LEAVE_BALANCE_STATS,
      }),
      providesTags: ["msGetLeaveBalanceStats"],
    }),
    msGetAllLeaveRequests: builder.query<TGetMSAllLeavesResponse, TGetMSAllLeavesRequest>({
      query: (params) => ({
        method: "GET",
        url: API_MS_GET_ALL_LEAVE_REQUESTS,
        params,
      }),
      providesTags: ["msGetAllLeaveRequests"],
    }),
    msCreateLeaveRequest: builder.mutation<TMSCreateLeaveRequestResponse, TMSCreateLeaveRequestData>({
      query: (body) => {
        const { attachment, fileId, ...rest } = body;
        const formData = new FormData();
        formData.append("startDate", rest.startDate);
        formData.append("endDate", rest.endDate);
        formData.append("leaveTypeId", String(rest.leaveTypeId));
        if (rest.reasonOfLeave) formData.append("reasonOfLeave", rest.reasonOfLeave);
        if (attachment instanceof File) formData.append("attachment", attachment);
        else if (fileId) formData.append("fileId", fileId);
        return {
          url: API_MS_CREATE_LEAVE_REQUEST,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["msGetAllLeaveRequests"],
    }),
    msCancelLeaveRequest: builder.mutation<TCancelMSLeaveRequestResponse, ICancelMSLeaveRequestParams>({
      query: (params) => ({
        url: API_MS_CREATE_LEAVE_REQUEST + `/${params.leaveId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["msGetAllLeaveRequests"],
    }),
    msGetLeaveRequestById: builder.query<TGetMSLeaveRequestByIdResponse, { leaveId: string | number }>({
      query: ({ leaveId }) => ({
        url: API_MS_CREATE_LEAVE_REQUEST + `/${leaveId}`,
      }),
      providesTags: ["msGetLeaveRequestById"],
    }),
    msUpdateLeaveRequest: builder.mutation<TUpdateMSLeaveRequestResponse, TUpdateMSLeaveRequestParams>({
      query: (body) => ({
        url: API_MS_CREATE_LEAVE_REQUEST + `/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["msGetAllLeaveRequests", "msGetLeaveRequestById"],
    }),
    msGetAllLeaveTypes: builder.query<IApiResponse<ILeaveType[]>, void>({
      query: () => ({
        url: API_MS_LEAVE_REQUEST_TYPES,
      }),
    }),
    msGetLeaveRequestSummary: builder.query<IApiResponse<ILeaveSummary>, void>({
      query: () => ({
        url: API_MS_LEAVE_REQUEST_SUMMARY,
      }),
    }),
    msGetLeavePolicy: builder.query<
      TMsGetLeavePolicyResponse,
      { leaveTypeId: number }
    >({
      query: ({ leaveTypeId }) => ({
        url: API_MS_LEAVE_POLICY,
        params: { leaveTypeId },
      }),
    }),
    msRequestAmendment: builder.mutation<
      TMSRequestAmendmentResponse,
      TMSRequestAmendmentParams
    >({
      query: ({ leaveId, ...body }) => ({
        url: API_MS_CREATE_LEAVE_REQUEST + `/${leaveId}/amendment`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["msGetAllLeaveRequests", "msGetLeaveRequestById"],
    }),
    msCancelAmendment: builder.mutation<
      TMSCancelAmendmentResponse,
      TMSCancelAmendmentParams
    >({
      query: ({ leaveId, amendmentId }) => ({
        url: API_MS_CREATE_LEAVE_REQUEST + `/${leaveId}/amendment/${amendmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["msGetAllLeaveRequests", "msGetLeaveRequestById"],
    }),
  }),
});

export const {
  useMsGetLeaveBalanceStatsQuery,
  useMsGetAllLeaveRequestsQuery,
  useMsCreateLeaveRequestMutation,
  useMsCancelLeaveRequestMutation,
  useMsGetLeaveRequestByIdQuery,
  useMsUpdateLeaveRequestMutation,
  useMsGetAllLeaveTypesQuery,
  useLazyMsGetAllLeaveTypesQuery,
  useMsGetLeaveRequestSummaryQuery,
  useMsGetLeavePolicyQuery,
  useMsRequestAmendmentMutation,
  useMsCancelAmendmentMutation,
} = leaveRequestApi;
